'use client'

import { useState, useEffect, useRef } from 'react'
import { useUser, useClerk } from '@clerk/nextjs'
import Link from 'next/link'
import { ChevronDown, X, User, Settings, LogOut, ExternalLink, HelpCircle, Mail, Newspaper } from 'lucide-react'

export default function AccountDropdown() {
  const { user, isLoaded } = useUser()
  const { signOut } = useClerk()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Dynamic Greeting Logic
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
  }

  // Click Outside Detection
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!isLoaded || !user) return null

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button 
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex items-center gap-2 px-3 py-1.5 border border-border/60 hover:border-brand/40 hover:bg-muted/30 transition-all rounded-sm group shadow-sm bg-background/50"
      >
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-foreground">Account</span>
        <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu (NYT Style) */}
      {isOpen && (
        <div 
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-950 border border-border shadow-2xl z-[100] animate-in fade-in slide-in-from-top-2 duration-200"
        >
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border/40">
            <span className="text-[10px] font-bold text-muted-foreground truncate max-w-[200px]">{user.primaryEmailAddress?.emailAddress}</span>
            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-muted rounded-full transition-colors">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Greeting Section */}
          <div className="p-6 pb-4">
            <h3 className="text-2xl text-foreground leading-tight font-serif italic tracking-tight underline transition-all decoration-brand/30">
              {getGreeting()}, <span className="font-bold not-italic font-playfair">{user.firstName || 'Member'}</span>.
            </h3>
            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed italic">
              Access to the institutional dossier is limited based on your current authorization level.
            </p>
          </div>

          {/* Navigation Links */}
          <div className="py-2">
            <Link 
              href="/account" 
              className="flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Settings className="w-4 h-4 text-muted-foreground group-hover:text-brand" />
                <span className="text-sm font-medium">Account settings</span>
              </div>
              <ExternalLink className="w-3 h-3 text-muted-foreground/30" />
            </Link>

            <div className="px-6 py-4 my-2 border-y border-border/40 bg-muted/20">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 mb-3">Institutional Content</p>
              <div className="space-y-3">
                <Link 
                  href="/account/saved" 
                  className="flex items-center gap-3 text-xs font-bold hover:text-brand transition-colors text-muted-foreground/80"
                >
                  <Newspaper className="w-3.5 h-3.5" />
                  Saved articles
                </Link>
                <Link 
                  href="/newsletter" 
                  className="flex items-center gap-3 text-xs font-bold hover:text-brand transition-colors text-muted-foreground/80"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Newsletters
                </Link>
              </div>
            </div>

            <a 
              href="mailto:support@worldprimepost.com"
              className="flex items-center gap-3 px-6 py-3 hover:bg-muted/50 transition-colors text-muted-foreground/80 group"
            >
              <HelpCircle className="w-4 h-4 group-hover:text-brand" />
              <span className="text-sm">Help Center</span>
            </a>
          </div>

          {/* Footer (Logout) */}
          <div className="p-4 bg-muted/10 border-t border-border/40">
            <button 
              onClick={() => {
                setIsOpen(false)
                signOut()
              }}
              className="text-xs font-bold text-foreground hover:text-red-500 underline underline-offset-4 transition-colors p-2"
            >
              Log out
            </button>
          </div>

        </div>
      )}
    </div>
  )
}
